// src/lib/managers/conversation/project.ts

/**
 * Le projet d'une conversation de l'assistant (Api-Ai : docs/refonte-assistant/CONCEPTION-projets.md) :
 * un espace de l'utilisateur, avec un nom, des consignes qu'il a écrites et un périmètre de mails (des
 * adresses de contacts).
 *
 * Deux choses se passent ici, sans modèle :
 *  - ce que l'app envoie du projet à l'Api-Ai avec chaque appel d'un tour (`toEtapesProjet`) ;
 *  - le périmètre appliqué à une lecture de mails (`applyMailScope`) : dans une conversation de projet,
 *    une lecture ne porte que sur les mails échangés avec les adresses du projet. C'est une règle de
 *    l'app, pas une indication donnée au modèle.
 */

import type { ConversationEtapesProjetDTO } from "../api/types/apiResponse";
import type { ViewFilter } from "../customView";
import type { AssistantProject } from "./types/conversation";

/** Ce que l'app dit à l'Api-Ai d'une lecture faite dans un projet (`perimetre` du résultat). */
export type MailScopeOutcome = "projet" | "hors";

/** Le projet tel qu'il part avec chaque appel du tour : son nom, et ce qu'il a de consignes et de périmètre. */
export function toEtapesProjet(
    project: AssistantProject,
): ConversationEtapesProjetDTO {
    return {
        nom: project.name,
        ...(project.instructions ? { consignes: project.instructions } : {}),
        ...(project.mailScope.length > 0
            ? { perimetre: project.mailScope }
            : {}),
    };
}

/**
 * Une adresse du périmètre répond-elle à ce qui est demandé ? Une adresse demandée : la même. Un terme
 * que les contacts n'ont pas résolu (« dupont », cherché tel quel dans les adresses) : il y figure.
 */
const matchesRequested = (address: string, requested: string): boolean =>
    requested.includes("@")
        ? address === requested
        : address.includes(requested);

/**
 * Le périmètre d'un projet appliqué au filtre d'une lecture de mails.
 *
 * Le côté limité est celui du correspondant : l'expéditeur pour la boîte de réception, le destinataire
 * pour les mails envoyés.
 *  - rien de demandé de ce côté (« mes non lus ») : toutes les adresses du projet ;
 *  - des adresses demandées : celles du projet qui y répondent ;
 *  - aucune n'y répond (« les mails de Paul », hors projet) : `outcome` « hors », rien n'est à lire.
 * Sans périmètre : le filtre est rendu tel quel, sans `outcome`.
 */
export function applyMailScope(
    filter: ViewFilter,
    mailScope: string[],
    sent: boolean,
): { filter: ViewFilter; outcome?: MailScopeOutcome } {
    const scope = [
        ...new Set(mailScope.map((address) => address.trim().toLowerCase())),
    ].filter(Boolean);
    if (scope.length === 0) return { filter };

    const side = sent ? "identifiedRecipients" : "identifiedSenders";
    const requested = (filter[side] ?? []).map((value) =>
        value.trim().toLowerCase(),
    );
    const kept =
        requested.length === 0
            ? scope
            : scope.filter((address) =>
                  requested.some((value) => matchesRequested(address, value)),
              );
    return kept.length === 0
        ? { filter, outcome: "hors" }
        : { filter: { ...filter, [side]: kept }, outcome: "projet" };
}
